import { useCallback, useState } from 'react';

import { BulkActionResponse, ProductOut } from '../../store/api';
import { FallbackPriceTier, mapFallbackPriceTierOutToFallbackPriceTier, PriceTier } from '../../../models/PriceTier';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { CheckBeforeAddingModalProps } from './CheckBeforeAddingModal';
import { UnavailableProductsModalProps } from './UnavailableProductsModal';

type Props<T> = {
    /**
     * Callback for changing a price.
     */
    readonly onAction: (performChecks: boolean, options: T) => Promise<BulkActionResponse>;
};

type UseListContentChecks<T> = {
    readonly changePrice: (options: T) => Promise<void>;
    readonly getCheckBeforeAddingModalProps: () => CheckBeforeAddingModalProps;
    readonly getUnavailableProductsModalProps: () => UnavailableProductsModalProps;
};

type CheckData = {
    readonly fallbackPriceTiers: FallbackPriceTier[];
    readonly alreadyInListProducts: ProductOut[];
    readonly preOrderedProducts: ProductOut[];
};

const emptyCheckData: CheckData = {
    fallbackPriceTiers: [],
    alreadyInListProducts: [],
    preOrderedProducts: []
};

type Options = { priceTier?: PriceTier; target?: ListActionTarget };

export function useListContentChecks<T extends Options>({ onAction }: Props<T>): UseListContentChecks<T> {
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState<T | undefined>(undefined);
    const [checkData, setCheckData] = useState<CheckData>(emptyCheckData);
    const [unavailableProducts, setUnavailableProducts] = useState<ProductOut[]>([]);

    const reset = useCallback(() => {
        setOptions(undefined);
        setCheckData(emptyCheckData);
        setUnavailableProducts([]);
    }, []);

    const changePrice = useCallback(
        async (options: T) => {
            setOptions(options);
            setIsLoading(true);

            try {
                const response = await onAction(true, options);

                const fallbackPriceTiers = response.fallbackPriceTiers ?? [];
                const alreadyInListProducts = response.alreadyInListProducts ?? [];
                const preOrderedProducts = response.preOrderedProducts ?? [];
                const unavailableProducts = response.unavailableProducts ?? [];

                if (
                    fallbackPriceTiers.length === 0 &&
                    alreadyInListProducts.length === 0 &&
                    preOrderedProducts.length === 0
                ) {
                    setOptions(undefined);
                    setCheckData(emptyCheckData);
                    setUnavailableProducts(unavailableProducts);
                } else {
                    setCheckData({
                        fallbackPriceTiers: mapFallbackPriceTierOutToFallbackPriceTier(fallbackPriceTiers),
                        alreadyInListProducts,
                        preOrderedProducts
                    });
                    setUnavailableProducts([]);
                }
            } catch (e) {
                reset();
            } finally {
                setIsLoading(false);
            }
        },
        [onAction, reset]
    );

    const onProceed = useCallback(async () => {
        if (options) {
            setIsLoading(true);

            try {
                const response = await onAction(false, options);

                setUnavailableProducts(response.unavailableProducts ?? []);
            } finally {
                setIsLoading(false);
                setOptions(undefined);
                setCheckData(emptyCheckData);
            }
        }
    }, [onAction, options]);

    return {
        changePrice,
        getCheckBeforeAddingModalProps: () => ({
            isLoading,
            requestedPriceTier: options?.priceTier,
            target: options?.target,
            fallbackPriceTiers: checkData.fallbackPriceTiers,
            alreadyInListProducts: checkData.alreadyInListProducts,
            preOrderedProducts: checkData.preOrderedProducts,
            onProceed,
            onCancel: reset
        }),
        getUnavailableProductsModalProps: () => ({ unavailableProducts, onDismiss: reset })
    };
}
